import { parentOf } from '../stack';
import type { Layer, TemplateDoc } from '../template';
import { sceneTimeline } from './compile';
import { docMotionSchema } from './schema';

/**
 * Scenes as the storyboard sees them: each is a design of its own (its layers, plus the layers
 * that stay on screen for the whole video), shown one after another.
 */

/** Scene of a layer: its own, or the one of the stack it sits in. None: the whole video. */
export function layerScene(doc: TemplateDoc, layer: Layer): string | undefined {
  let l: Layer | undefined = layer;
  for (let i = 0; l && i < 50; i++) {
    if (l.motion?.scene) return l.motion.scene;
    l = parentOf(doc, l);
  }
  return undefined;
}

/** The layers shown during a scene: its own and the ones on screen for the whole video. */
export function docForScene(doc: TemplateDoc, sceneId: string | null | undefined): TemplateDoc {
  if (!sceneId || !doc.motion?.scenes?.some((s) => s.id === sceneId)) return doc;
  return {
    ...doc,
    layers: doc.layers.filter((l) => {
      const s = layerScene(doc, l);
      return !s || s === sceneId;
    }),
  };
}

/**
 * The document as a still picture (thumbnails, image renders, previews): a video made of scenes
 * shows its poster scene (the first one by default), not every scene stacked on top of each other.
 */
export function stillDoc(doc: TemplateDoc): TemplateDoc {
  const scenes = doc.motion?.scenes ?? [];
  const poster = scenes.find((s) => s.id === doc.motion?.poster) ?? scenes[0];
  return poster ? docForScene(doc, poster.id) : doc;
}

/** When a layer appears in the video (the start of its scene), ms. */
export function layerStart(doc: TemplateDoc, layer: Layer): number {
  const scene = layerScene(doc, layer);
  if (!scene || !doc.motion?.scenes?.length) return 0;
  const planned = sceneTimeline(docMotionSchema.parse(doc.motion)).scenes.find(
    (s) => s.id === scene,
  );
  return planned?.start ?? 0;
}

/** How long a layer is on screen, ms: its scene, or the whole video. */
export function layerWindow(doc: TemplateDoc, layer: Layer): [number, number] {
  const timeline = sceneTimeline(docMotionSchema.parse(doc.motion ?? {}));
  const scene = layerScene(doc, layer);
  const planned = scene ? timeline.scenes.find((s) => s.id === scene) : undefined;
  return planned ? [planned.start, planned.end] : [0, timeline.duration];
}

export function newSceneId(): string {
  return `scene-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}
